/* Tabbed product carousel (featured collections). Reimplements the reference's
   observed behavior: the active tab is the primary button and is disabled (so
   the hover fill machinery skips it), switching rises the incoming panel 2rem
   over 0.15s, and the arrow pair pages the visible slider by one full view —
   disabling, undimmed, at either end.

   Guarded module: two instances on a page emit this tag twice and both execute;
   a bare top-level class would throw on redeclaration before any define guard. */
if (!customElements.get('tabs-element')) {
  customElements.define('tabs-element', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
      this.prevButton = this.querySelector('[data-prev]');
      this.nextButton = this.querySelector('[data-next]');
      if (!this.tabs.length) return;

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        /* The reference has no tab keyboard handling; ours moves focus only —
           selection still happens on click/Enter — skipping the disabled active
           tab so focus is never stranded on an unfocusable element. */
        tab.addEventListener('keydown', (event) => {
          const delta = { ArrowLeft: -1, ArrowRight: 1 }[event.key];
          if (!delta) return;
          event.preventDefault();
          let next = index;
          do {
            next = (next + delta + this.tabs.length) % this.tabs.length;
          } while (this.tabs[next].disabled && next !== index);
          this.tabs[next].focus();
        });
      });

      this.panels.forEach((panel) => {
        const scroller = panel?.querySelector('.featured-collections__scroller');
        if (scroller) scroller.addEventListener('scroll', () => {
          if (!panel.hidden) this.updateArrows();
        }, { passive: true });
      });

      this.prevButton?.addEventListener('click', () => this.page(-1));
      this.nextButton?.addEventListener('click', () => this.page(1));
      window.addEventListener('resize', () => this.updateArrows());

      this.select(0, true);
    }

    select(index, instant) {
      this.selected = index;
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.classList.toggle('button--primary', active);
        tab.classList.toggle('button--secondary', !active);
        tab.setAttribute('aria-selected', String(active));
        /* Reference state machine: the active tab is disabled. */
        tab.disabled = active;
      });
      this.panels.forEach((panel, i) => {
        if (!panel) return;
        const show = i === index;
        if (panel.hidden === !show) return;
        panel.hidden = !show;
        if (show && !instant && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          panel.animate(
            { transform: ['translateY(2rem)', 'translateY(0)'], opacity: [0, 1] },
            { duration: 150, easing: 'ease' }
          );
        }
      });
      this.updateArrows();
    }

    activeScroller() {
      return this.panels[this.selected]?.querySelector('.featured-collections__scroller');
    }

    /* Reference paging: one full view of whole items, measured from the first
       two slides' offset delta so the gap is included. */
    page(direction) {
      const scroller = this.activeScroller();
      if (!scroller) return;
      const slides = scroller.querySelectorAll('.featured-collections__slide');
      if (slides.length < 2) return;
      const offset = slides[1].offsetLeft - slides[0].offsetLeft;
      const styles = getComputedStyle(scroller);
      const view = scroller.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
      const perPage = Math.max(1, Math.floor(view / offset));
      scroller.scrollBy({ left: direction * perPage * offset, behavior: 'smooth' });
    }

    updateArrows() {
      const scroller = this.activeScroller();
      if (!scroller || !this.prevButton) return;
      this.prevButton.disabled = scroller.scrollLeft <= 1;
      this.nextButton.disabled = scroller.scrollLeft >= scroller.scrollWidth - scroller.clientWidth - 1;
    }
  });
}
